<script setup>
// W2 — reações da mensagem, sob a bolha.
//
// O backend guarda em content_attributes.reactions o mapa emoji → lista de
// quem reagiu (Waha::MessageReactionIngest). Quem reagiu pelo painel entra
// como 'agent', e é isso que permite destacar a reação da equipe e saber o
// que remover ao clicar de novo — contador puro não daria para desfazer.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  // content_attributes.reactions
  reactions: { type: Object, default: () => ({}) },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle']);

const { t } = useI18n();

const AGENT_AUTHOR = 'agent';

// Ordem estável: mais reagido primeiro e, no empate, ordem alfabética do
// emoji — sem isso a lista dança a cada atualização do websocket.
const chips = computed(() =>
  Object.entries(props.reactions || {})
    .map(([emoji, authors]) => {
      const list = Array.isArray(authors) ? authors : [];
      return {
        emoji,
        count: list.length,
        reactedByAgent: list.includes(AGENT_AUTHOR),
      };
    })
    .filter(chip => chip.count > 0)
    .sort((a, b) => b.count - a.count || a.emoji.localeCompare(b.emoji))
);

const titleFor = chip =>
  chip.reactedByAgent
    ? t('CONVERSATION.REACTIONS.REMOVE_YOURS', { emoji: chip.emoji })
    : t('CONVERSATION.REACTIONS.REACT_WITH', { emoji: chip.emoji });
</script>

<template>
  <!-- Wrapper sempre presente: v-if na raiz sem v-else confunde o pai que
       posiciona este bloco na grid da mensagem. -->
  <div class="contents">
    <div v-if="chips.length" class="flex flex-wrap items-center gap-1">
      <button
        v-for="chip in chips"
        :key="chip.emoji"
        type="button"
        :disabled="disabled"
        :title="titleFor(chip)"
        :aria-pressed="chip.reactedByAgent"
        data-testid="message-reaction-chip"
        class="reaction-chip flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] leading-none transition-colors disabled:cursor-not-allowed disabled:opacity-60"
        :class="
          chip.reactedByAgent
            ? 'border-n-blue-8 bg-n-blue-3 text-n-blue-11'
            : 'border-n-weak bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
        "
        @click.stop="emit('toggle', chip.emoji)"
      >
        <span class="text-[13px] leading-none">{{ chip.emoji }}</span>
        <span v-if="chip.count > 1" class="tabular-nums font-semibold">
          {{ chip.count }}
        </span>
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Entra com um pulinho curto: reação que aparece sem movimento passa
   despercebida no meio do fio. */
@keyframes reaction-pop {
  from {
    opacity: 0;
    transform: scale(0.7);
  }
  60% {
    transform: scale(1.06);
  }
}
.reaction-chip {
  animation: reaction-pop 0.18s cubic-bezier(0.2, 0.7, 0.3, 1) both;
}
@media (prefers-reduced-motion: reduce) {
  .reaction-chip {
    animation: none;
  }
}
</style>
